/**
 * Chat Types
 *
 * Type definitions for chat state
 */
import type {
  FareCalendarTemplateData,
  FlightsListingTemplateData,
  FlightsListingSplitViewTemplateData,
  FlightsReviewTemplateData,
  FlightsReviewInterstitialTemplateData,
  FlightsReviewTravellersTemplateData,
  FlightThankYouResponse,
  RouteOptionsTemplateData,
  HotelDetailsTemplateData,
  HotelReviewTemplateData,
  HotelRoomSelectionTemplateData,
  HotelsListingTemplateData,
  HotelCouponUpdateTemplateData,
  HotelTravellerDetailTemplateData,
  HotelGstDetailTemplateData,
  HotelPanDetailTemplateData,
  HotelCheckoutTemplateData,
  HotelThankYouResponse,
  ItineraryTemplateData,
  TripGuideTemplateData,
} from '@contracts';

// ============================================
// Message Types
// ============================================

export interface TextContent {
  type: 'text';
  value: string;
}

export interface MarkdownContent {
  type: 'markdown';
  value: string;
}

// ============================================
// API Call Types
// ============================================

/**
 * API call identifier - determines which API endpoint to call
 */
export enum ApiCallIdentifier {
  GET_FLIGHT_LISTING = 'GET_FLIGHT_LISTING',
  GET_FLIGHT_REVIEW = 'GET_FLIGHT_REVIEW',
}
/**
 * Flight listing API call payload
 * Calls sendMessageThunk with the query
 */
export interface GetFlightListingPayload {
  apiCallIdentifier: ApiCallIdentifier.GET_FLIGHT_LISTING;
  query: string;
}

/**
 * Flight review API call payload
 * Calls flights review API endpoint
 */
export interface GetFlightReviewPayload {
  apiCallIdentifier: ApiCallIdentifier.GET_FLIGHT_REVIEW;
  contextHashKey: string;
  crId: string;
}

export enum ActionType {
  API_CALL = 'API_CALL',
}

/**
 * Union type for all API call payloads
 */
export type ApiCallPayload = GetFlightListingPayload | GetFlightReviewPayload;

export interface CardContent {
  type: 'card';
  thumbnail: {
    imageUrl: string;
    label: string;
    // TODO: Not integrated with the client backend yet
    actionType?: ActionType;
    actionPayload?: ApiCallPayload;
  };
  templateType?: TemplateType;
  templateData?: TemplateData;
}

/**
 * Leading question item - clickable question/answer pair.
 */
export interface LeadingQuestionItem {
  question: string;
  answer: string;
}

/**
 * Leading question content - contains clickable question/answer pairs.
 * When user clicks a question, the answer value is sent as the query.
 */
export interface LeadingQuestionContent {
  type: 'leading-question';
  questions: Array<LeadingQuestionItem>;
}

// ============================================
// Action Button Types
// ============================================

export interface ApiCallAction {
  type: 'api-call';
  endpoint: string;
  method: 'GET' | 'POST';
  params?: Record<string, string>;
}

export type ActionButtonAction = ApiCallAction;

export interface ActionButtonContent {
  type: 'action-button';
  label: string;
  action: ActionButtonAction;
}

export type ContentItem =
  | MarkdownContent
  | TextContent
  | CardContent
  | LeadingQuestionContent
  | ActionButtonContent;

export interface UserMessage {
  id: string;
  role: 'user';
  content: string;
  timestamp: string; // ISO string for Redux serialization
}
/**
 * Async task descriptor — signals frontend to start polling for results.
 * Present only when backend cannot return the full response in a single stream event.
 */
export type PollHttpMethod = 'GET' | 'POST';

export interface AsyncTask {
  taskId: string;
  taskType: 'FLIGHT_SEARCH';
  pollEndpoint: string;
  pollMethod: PollHttpMethod;
  params?: Record<string, string>;
  pollIntervalMs?: number;
  maxAttempts?: number;
  maxNetworkRetries?: number;
  networkRetryDelayMs?: number;
}

export interface AssistantMessage {
  id: string;
  // TODO: get role node instead of type node from cb and use role only
  role: 'assistant';
  // TODO: get removed type node from cb and use role only
  type?: string;
  content: Array<ContentItem>;
  activeTemplate?: TemplateType;
  templateData?: TemplateData;
  trackingInfo?: TrackingInfo;
  timestamp: string; // ISO string for Redux serialization
  meta?: {
    receivedAt: number;
    finishedAt: number;
  };
  asyncTask?: AsyncTask;
}

/**
 * Backend-driven tracking info contract.
 * Sent by CBackend in chat stream FINAL_RESPONSE and template API responses.
 *
 * Structured to support multiple tracking systems (omniture, pdt, xdm).
 * Currently only omniture is implemented; pdt and xdm are reserved for future use.
 */
export interface TrackingInfo {
  common?: CommonTrackingData;
  pageLoad?: PageLoadTrackingData;
}

export interface CommonTrackingData {
  omniture?: Record<string, string>;
  /** @reserved PDT common tracking data — not yet implemented */
  pdt?: Record<string, string>;
  /** @reserved XDM common tracking data — not yet implemented */
  xdm?: Record<string, unknown>;
}

export interface PageLoadTrackingData {
  omniture?: Array<Record<string, string>>;
  /** @reserved PDT page load events — not yet implemented */
  pdt?: Array<Record<string, string>>;
  /** @reserved XDM page load events — not yet implemented */
  xdm?: Array<XdmEvent>;
}

export interface XdmEvent {
  eventType: string;
  eventName: string;
  eventValue: string;
  components?: Record<string, unknown>;
}

export type MessageType = AssistantMessage | UserMessage;

// ============================================
// Template Types
// ============================================

export type TemplateType =
  | 'flights-listing'
  | 'flights-listing-split'
  | 'flights-listing-simple'
  | 'flights-review'
  | 'flights-review-interstitial'
  | 'flights-review-travellers'
  | 'route-options'
  | 'fare-calendar'
  | 'hotels-details'
  | 'hotels-room-selection'
  | 'hotels-listing'
  | 'hotels-coupon-update'
  | 'hotels-traveller-detail'
  | 'hotels-gst-detail'
  | 'hotels-pan-detail'
  | 'hotels-checkout'
  | 'hotels-qna'
  | 'itinerary-plan'
  | 'trip-guide'
  | 'hotels-review'
  | 'hotels-review-error'
  | 'flights-thankyou'
  | 'hotels-thankyou';

export type ActiveTemplate = TemplateType | null | undefined;

export type TemplateData =
  | FareCalendarTemplateData
  | FlightsListingTemplateData
  | FlightsListingSplitViewTemplateData
  | FlightsReviewTemplateData
  | FlightsReviewInterstitialTemplateData
  | FlightsReviewTravellersTemplateData
  | FlightThankYouResponse
  | RouteOptionsTemplateData
  | HotelDetailsTemplateData
  | HotelReviewTemplateData
  | HotelRoomSelectionTemplateData
  | HotelsListingTemplateData
  | HotelCouponUpdateTemplateData
  | HotelTravellerDetailTemplateData
  | HotelGstDetailTemplateData
  | HotelPanDetailTemplateData
  | HotelCheckoutTemplateData
  | HotelThankYouResponse
  | ItineraryTemplateData
  | TripGuideTemplateData
  | null
  | undefined;
